import { useMemo } from 'react'
import { Icon } from '@iconify/react'
import {
  addContemplatedPrizeImage,
  buildContemplatedPrizeProgressMap,
  ensureDateWinningSchedules,
  removeContemplatedPrizeImage,
  updateContemplatedPrizeImage,
} from '@/lib/raspadinha-campaigns'
import { RASPADINHA_EXAMPLE_CAMPAIGN_LABEL } from '@/lib/raspadinha-campaigns/raspadinha-example-label'
import type {
  RaspadinhaContemplatedPrizeImage,
  RaspadinhaDayWinningSchedule,
} from '@/types/raspadinha-campaign'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'
import { RaspadinhaContemplatedPrizeProgressBar } from './RaspadinhaContemplatedPrizeProgressBar'
import { RaspadinhaImageUploadField } from './RaspadinhaImageUploadField'
import styles from './RaspadinhaContemplatedPrizesEditor.module.css'

type RaspadinhaContemplatedPrizesEditorProps = {
  prizes: RaspadinhaContemplatedPrizeImage[]
  schedules: RaspadinhaDayWinningSchedule[]
  startsAt: string | null
  endsAt: string | null
  onPrizesChange: (next: RaspadinhaContemplatedPrizeImage[]) => void
  onPrizeImageChange: (index: number, url: string, file?: File | null) => void
  disabled?: boolean
}

export function RaspadinhaContemplatedPrizesEditor({
  prizes,
  schedules,
  startsAt,
  endsAt,
  onPrizesChange,
  onPrizeImageChange,
  disabled = false,
}: RaspadinhaContemplatedPrizesEditorProps) {
  const resolvedSchedules = useMemo(
    () => ensureDateWinningSchedules(schedules, startsAt, endsAt),
    [endsAt, schedules, startsAt]
  )

  const progressByPrizeId = useMemo(
    () => buildContemplatedPrizeProgressMap(resolvedSchedules),
    [resolvedSchedules]
  )

  const handleAddPrize = () => {
    onPrizesChange(addContemplatedPrizeImage(prizes))
  }

  const handleRemovePrize = (prizeId: string) => {
    onPrizesChange(removeContemplatedPrizeImage(prizes, prizeId))
  }

  const handleUpdatePrize = (
    prizeId: string,
    patch: Partial<Omit<RaspadinhaContemplatedPrizeImage, 'id'>>
  ) => {
    onPrizesChange(updateContemplatedPrizeImage(prizes, prizeId, patch))
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h4 className={styles.title}>Prêmios contemplados</h4>
          <p className={styles.hint}>
            Cadastre uma ou mais imagens exibidas quando o participante ganha. A barra abaixo do
            título mostra quantas contemplações estão previstas nos horários e quantas já foram
            registradas.
          </p>
        </div>
        <button type="button" className={styles.addButton} onClick={handleAddPrize} disabled={disabled}>
          <Icon icon="mdi:plus" aria-hidden />
          Adicionar prêmio
        </button>
      </div>

      {prizes.length === 0 ? (
        <p className={styles.emptyState}>Nenhum prêmio contemplado cadastrado.</p>
      ) : (
        <ul className={styles.prizeList}>
          {prizes.map((prize, index) => {
            const progress = progressByPrizeId.get(prize.id) ?? {
              scheduledCount: 0,
              fulfilledCount: 0,
            }
            const prizeLabel = prize.title.trim() || `Prêmio ${index + 1}`

            return (
            <li key={prize.id} className={styles.prizeCard}>
              <div className={styles.prizeCardHeader}>
                <span className={styles.prizeIndex}>Prêmio {index + 1}</span>
                <button
                  type="button"
                  className={styles.removePrizeButton}
                  onClick={() => handleRemovePrize(prize.id)}
                  disabled={disabled}
                  aria-label={`Remover prêmio ${index + 1}`}
                >
                  <Icon icon="mdi:trash-can-outline" aria-hidden />
                  Remover
                </button>
              </div>

              <div className={styles.prizeCardBody}>
                <RaspadinhaImageUploadField
                  className={styles.prizeImageUpload}
                  label="Imagem do prêmio"
                  hint="(Exibida quando o participante contempla este prêmio)"
                  imageUrl={prize.image_url}
                  onImageChange={(url, file) => onPrizeImageChange(index, url, file)}
                  previewSize="compact"
                  disabled={disabled}
                />

                <div className={styles.prizeInfoColumn}>
                  <div className={editStyles.editField}>
                    <label className={editStyles.editLabel} htmlFor={`prize-title-${prize.id}`}>
                      Título do prêmio
                    </label>
                    <input
                      id={`prize-title-${prize.id}`}
                      type="text"
                      className={editStyles.editInput}
                      maxLength={120}
                      placeholder={`Ex: Garfo Inox ${RASPADINHA_EXAMPLE_CAMPAIGN_LABEL}`}
                      value={prize.title}
                      disabled={disabled}
                      onChange={(event) =>
                        handleUpdatePrize(prize.id, { title: event.target.value })
                      }
                    />
                  </div>

                  <RaspadinhaContemplatedPrizeProgressBar
                    fulfilledCount={progress.fulfilledCount}
                    scheduledCount={progress.scheduledCount}
                    prizeLabel={prizeLabel}
                  />
                </div>
              </div>
            </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
